import { NotebookConstants } from "constants/notebook-constants";
import { useAtom } from "jotai";
import NotebookRecord from "models/view-models/notebook-record";
import PageRecord from "models/view-models/page-record";
import { useCallback, useMemo } from "react";
import {
    notebooksAtom,
    notebooksLoadingAtom,
    notebooksLoadedAtom,
    currentPageAtom,
} from "utilities/atoms/notebooks-atom";
import { useGlobalState } from "utilities/contexts/use-global-state-context";
import useLastVisitedNotebook from "utilities/hooks/domain/notebooks/use-last-visited-notebook";
import usePageErrors from "utilities/hooks/use-page-errors";
import ServiceHookResult from "utilities/interfaces/service-hook-result";
import { t } from "utilities/localization-utils";
import NotebookService from "utilities/services/notebooks/notebook-service";
import PageService from "utilities/services/notebooks/page-service";

interface UseNotebooksAtom extends ServiceHookResult<NotebookRecord[]> {
    addPageToNotebook: (notebookId: number, newPage) => void;
    createNotebook: (
        notebook: NotebookRecord
    ) => Promise<NotebookRecord | undefined>;
    firstNotebookInList: NotebookRecord | undefined;
    lastVisitedNotebook: NotebookRecord | undefined;
    refresh: () => Promise<void>;
    renameNotebook: (notebookId: number, newName: string) => Promise<void>;
    removeNotebook: (notebookId: number) => void;
    renamePage: (pageId: number, newName: string) => Promise<void>;
    removePageFromNotebook: (deletedPageId: number, notebookId: number) => void;
    replacePage: (page: PageRecord) => void;
    updateNotebook: (updatedNotebook: NotebookRecord) => void;
    updatePage: (page: PageRecord) => Promise<void>;
}

export default function useNotebooksAtom(): UseNotebooksAtom {
    const {
        create: createNotebookApi,
        update: updateNotebookApi,
        list: listNotebookApi,
    } = NotebookService;
    const { update: updatePageApi } = PageService.useUpdate();
    const { list: listSharedWithMePagesApi } = PageService.useSharedWithMe();

    const {
        handlePageLoadError,
        pageErrors: errors,
        resetPageErrors,
        setPageErrors,
    } = usePageErrors();
    const [notebooks, setNotebooks] = useAtom(notebooksAtom);
    const [loading, setLoading] = useAtom(notebooksLoadingAtom);
    const [loaded, setLoaded] = useAtom(notebooksLoadedAtom);
    const { globalState } = useGlobalState();
    const { lastVisitedNotebookId } = useLastVisitedNotebook();
    const [currentPage, setCurrentPageAtom] = useAtom(currentPageAtom);

    // #region Notebook CRUD

    const createNotebook = useCallback(
        async (notebook: NotebookRecord) => {
            try {
                const result = await createNotebookApi(notebook);
                const newNotebook = result.resultObject;
                if (result.result?.errors) {
                    throw Error;
                }
                if (result && newNotebook) {
                    setNotebooks((prevNotebooks) => [
                        ...prevNotebooks,
                        newNotebook,
                    ]);
                }
                resetPageErrors();
                return newNotebook;
            } catch (error) {
                handlePageLoadError(error);
                return undefined;
            }
        },
        [createNotebookApi, handlePageLoadError, resetPageErrors, setNotebooks]
    );

    const getSharedPages = useCallback(async () => {
        try {
            const result = await listSharedWithMePagesApi();
            return result.resultObjects ?? [];
        } catch {
            return [];
        }
    }, [listSharedWithMePagesApi]);

    const getSharedNotebook = useCallback(async () => {
        const sharedPages = await getSharedPages();

        const sharedNotebook = new NotebookRecord().with({
            id: 0,
            name: t("sharedPages"),
            pages: sharedPages,
            ownerId: 0,
            owner: undefined,
        });

        return sharedNotebook;
    }, [getSharedPages]);

    const getAllNotebooks = useCallback(async () => {
        setLoading(true);
        try {
            const result = await listNotebookApi();
            const sharedNotebook = await getSharedNotebook();
            setNotebooks([sharedNotebook, ...result.resultObjects]);
            setLoading(false);
            setLoaded(true);
        } catch (error) {
            handlePageLoadError(error);
        }
    }, [
        getSharedNotebook,
        handlePageLoadError,
        listNotebookApi,
        setLoaded,
        setLoading,
        setNotebooks,
    ]);

    const renameNotebook = async (notebookId: number, newName: string) => {
        const notebookToRename = notebooks.find((n) => n.id === notebookId);
        if (notebookToRename == null) {
            throw new Error("Notebook not found");
        }

        if (notebookToRename.id === 0) {
            throw new Error("Renaming Shared Pages Notebook Not Permitted");
        }

        const updatedNotebookRecord = notebookToRename?.with({ name: newName });

        try {
            const response = await updateNotebookApi(updatedNotebookRecord!);
            if (response == null || response?.result?.errors) {
                throw new Error("Failed to rename notebook");
            }

            setNotebooks((prevNotebooks) =>
                prevNotebooks.map((notebook) =>
                    notebook.id === notebookToRename.id
                        ? updatedNotebookRecord!
                        : notebook
                )
            );
        } catch (error) {
            throw error; // TODO We should return an error buffer of keys the UI can respond to.
        }
    };

    const removeNotebook = (notebookId: number) => {
        const updatedNotebooks = notebooks.filter(
            (notebook) => notebook.id !== notebookId
        );
        setNotebooks(updatedNotebooks);
    };

    // #endregion Notebook CRUD

    // #region Page CRUD

    const renamePage = async (pageId: number, newName: string) => {
        const pageToUpdate = notebooks
            .flatMap((n) => n.pages ?? [])
            .find((p) => p.id === pageId);

        if (!pageToUpdate) {
            throw new Error("Page not found");
        }

        const updatedPageRecord = pageToUpdate?.with({ name: newName });
        try {
            const updateResult = await updatePageApi(updatedPageRecord);

            if (updateResult.result?.errors) {
                throw Error;
            }
            const result = updateResult;

            // update the page in the notebooks state
            if (result.resultObject) {
                replaceNotebookPage(updatedPageRecord);
            }
        } catch (error) {
            setPageErrors(["Page renaming failed."]);
        }
    };

    const updateCurrentPageAtom = (updatedPage) => {
        if (currentPage && updatedPage.id === currentPage?.id) {
            setCurrentPageAtom(updatedPage);
        }
    };

    const updatePage = async (pageRecordToUpdate: PageRecord) => {
        try {
            const updateResult = await updatePageApi(pageRecordToUpdate);

            if (updateResult.result?.errors) {
                throw Error;
            }
            const result = updateResult;
            // update the page in the notebooks state
            if (result.resultObject) {
                replaceNotebookPage(pageRecordToUpdate);
                updateCurrentPageAtom(pageRecordToUpdate);
            }
        } catch (error) {
            setPageErrors([
                t("errors-actionResource", {
                    action: t("updating"),
                    resource: t("page"),
                }),
            ]);
        }
    };

    const removePageFromNotebook = async (
        deletedPageId: number,
        notebookId: number
    ) => {
        const notebook = notebooks.find((n) => n.id === notebookId);
        if (!notebook) return;

        const updatedNotebook = notebook.with({
            pages: notebook.pages?.filter((p) => p.id !== deletedPageId),
        });

        setNotebooks((prevNotebooks) =>
            prevNotebooks.map((notebook) =>
                notebook.id === notebookId ? updatedNotebook! : notebook
            )
        );
    };

    const replacePage = async (page: PageRecord) => {
        const notebook = getNotebookFromPage(page);
        if (!notebook) {
            return;
        }

        const updatedNotebook = notebook.with({
            pages: notebook.pages?.map((p) => (p.id === page.id ? page : p)),
        });

        setNotebooks((prevNotebooks) =>
            prevNotebooks.map((notebook) =>
                notebook.id === updatedNotebook.id ? updatedNotebook : notebook
            )
        );
    };

    // #endregion Page CRUD

    // #region Atom Management Helpers

    const getNotebookById = (id: number): NotebookRecord | undefined => {
        return notebooks.find((notebook) => notebook.id === id);
    };

    const getNotebookFromPage = (
        page: PageRecord
    ): NotebookRecord | undefined => {
        const notebookId =
            page.ownerId === globalState.currentIdentity?.userId()
                ? page.notebookId
                : NotebookConstants.SharedPageNotebookId;
        return getNotebookById(notebookId);
    };

    const updateNotebook = (updatedNotebook: NotebookRecord) => {
        setNotebooks((previousNotebooks: NotebookRecord[]): NotebookRecord[] =>
            previousNotebooks.map(
                (notebook: NotebookRecord): NotebookRecord =>
                    notebook.id === updatedNotebook.id
                        ? updatedNotebook
                        : notebook
            )
        );
    };

    const addPageToNotebook = (notebookId: number, newPage) => {
        const notebook = getNotebookById(notebookId);

        if (notebook == null) {
            return;
        }

        const updatedNotebook = notebook.with({
            pages: notebook.pages ? [...notebook.pages, newPage] : [newPage],
        });

        updateNotebook(updatedNotebook);
    };

    const replaceNotebookPage = (page: PageRecord) => {
        const containingNotebook = getNotebookById(page.notebookId);

        if (containingNotebook == null) {
            return;
        }

        const updatedNotebook = containingNotebook.with({
            pages: containingNotebook.pages
                ? containingNotebook.pages.map((p) =>
                      p.id === page.id ? page : p
                  )
                : [page],
        });

        updateNotebook(updatedNotebook);
    };

    const firstNotebookInList = useMemo(
        () =>
            notebooks.filter(
                (notebook) =>
                    notebook.id !== NotebookConstants.SharedPageNotebookId
            )[0],
        [notebooks]
    );
    const lastVisitedNotebook = useMemo(
        () =>
            notebooks.find((notebook) => notebook.id === lastVisitedNotebookId),
        [lastVisitedNotebookId, notebooks]
    );

    // #endregion Helpers

    return {
        addPageToNotebook,
        createNotebook,
        errors,
        firstNotebookInList,
        lastVisitedNotebook,
        loaded,
        loading,
        refresh: getAllNotebooks,
        removeNotebook,
        removePageFromNotebook,
        renameNotebook,
        renamePage,
        replacePage,
        resultObject: notebooks,
        updateNotebook,
        updatePage,
    };
}
